import React from 'react';
import PropTypes from 'prop-types';
import uniqid from 'uniqid';

import { Box, Button } from 'grommet';
import { FormClose, FormAdd } from 'grommet-icons';

import { FormInput } from './ProductFormComponent';

const renderNewInput = () => ({ id: uniqid(), val: '', added: false });
const renderPrevInputs = (val) => ({ id: uniqid(), val, added: true });
const extractValues = (data) => data.filter((dt) => dt.added).map((dt) => dt.val);

const MultipleTextInputs = ({
  maxInputs = null,
  value = [],
  handleFormValues,
  focusHighlight,
  bgColor = 'var(--page-input-bg)',
  textColor = 'var(--page-input-text)',
  borderColor = null,
  placeholder = null,
  onPendingInputChange = null,
}) => {
  const [inputs, setInputs] = React.useState([renderNewInput()]);

  React.useEffect(() => {
    if (value && value.length) {
      const prevInputs = value.map((dt) => (renderPrevInputs(dt)));

      if (!maxInputs || prevInputs.length < maxInputs) {
        prevInputs.push(renderNewInput());
      }

      setInputs(prevInputs);
    }
  }, []);

  React.useEffect(() => {
    // Notify parent if there is a pending input with a value
    const hasPending = inputs.some((input) => !input.added && input.val.trim());
    onPendingInputChange(hasPending);
  }, [inputs, onPendingInputChange]);

  const addInput = () => {
    if (inputs[inputs.length - 1].val) {
      const addInputs = [...inputs];
      inputs[inputs.length - 1].added = true;

      if (!maxInputs || inputs.length < maxInputs) {
        addInputs.push(renderNewInput());
      }

      setInputs(addInputs);
      handleFormValues(extractValues(inputs));
    }
  };

  const removeInput = (index) => {
    const remInputs = inputs.filter((_, i) => i !== index);
    const { length } = remInputs;

    if (remInputs[length - 1].added) {
      remInputs.push(renderNewInput());
    }

    setInputs(remInputs);
    handleFormValues(extractValues(remInputs));
  };

  const handleValue = (index, e) => {
    const newInputs = [...inputs];
    newInputs[index].val = e.target.value;

    setInputs(newInputs);
  };

  return (
    <Box gap="small">
      {inputs.map(({ id, val, added }, i) => (
        <Box key={id} direction="row" gap="0.5rem" align="center">
          <Box flex pad={{ bottom: '0.75rem' }} justify="center">
            <FormInput
              inputAdded={added}
              disabled={added}
              value={val}
              onChange={(e) => handleValue(i, e)}
              highlight={focusHighlight}
              bgColor={bgColor}
              borderColor={borderColor}
              placeholder={placeholder}
              style={{ color: textColor }}
            />
          </Box>
          <Box width="1.5rem" align="center" justify="center" pad={{ bottom: '0.75rem' }}>
            {added && (
              <Button
                plain
                icon={<FormClose size="1.25rem" color="var(--page-input-icon)" />}
                onClick={() => removeInput(i)}
              />
            )}
            {!added && val.trim() && (
              <Button
                plain
                title="Confirm"
                onClick={() => addInput()}
                style={{
                  background: focusHighlight,
                  borderRadius: '50%',
                  padding: '0.2rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                icon={<FormAdd size="1rem" color="white" />}
              />
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

MultipleTextInputs.propTypes = {
  maxInputs: PropTypes.number,
  handleFormValues: PropTypes.func.isRequired,
  focusHighlight: PropTypes.string.isRequired,
  value: PropTypes.arrayOf(PropTypes.string.isRequired),
  bgColor: PropTypes.string,
  textColor: PropTypes.string,
  borderColor: PropTypes.string,
  placeholder: PropTypes.string,
  onPendingInputChange: PropTypes.func,
};

export default MultipleTextInputs;
